/* ------------------------------- transporte da ponte Trilha → Consultas ---
   O prompt de aprofundamento não cabe mais na URL. Medição sobre o conteúdo
   publicado: a pílula mais longa das trilhas com link (jur02-p009) tem 1.770
   caracteres; com a moldura do prompt e as quatro alternativas o texto chega
   a 2.391, e percent-encoded (cada acento custa 6 caracteres — %C3%A9 —, cada
   quebra de linha vira %0A) vira 3.675 — bem acima do limite prático seguro
   de ~2.000 caracteres de URL. Então o texto viaja por `localStorage`
   (compartilhado entre abas da mesma origem, ao contrário de sessionStorage)
   e a URL leva só uma referência curta: `/?consulta=<id>`, 25 caracteres.

   A chave é de USO ÚNICO: a Home lê e apaga. Recarregar a aba, ou colar o
   link de novo, abre o chat vazio — o que se quer, porque o texto é o
   contexto de UMA resposta dada numa lição, não um link permanente.

   Storage é melhor-esforço: pode lançar (modo restrito/quota) e pode estar
   limpo quando a aba abrir. Todo caminho tem saída — quem grava cai no
   transporte antigo (`?pergunta=` com um prompt reduzido) e quem lê abre o
   chat normalmente, vazio. */

const PREFIXO = 'licito.trilhaGuru.consulta.'

// Nomes dos parâmetros de URL que a Home entende. `pergunta` é o transporte
// ANTIGO (prompt inteiro na query string): continua funcionando — links já
// abertos, favoritados ou colados em algum lugar não podem quebrar — e é
// também o fallback de quando o storage não está disponível.
export const PARAM_CONSULTA = 'consulta'
export const PARAM_PERGUNTA = 'pergunta'

// Janela de vida da chave: o suficiente para a aba nova abrir (é imediato) e
// curto o bastante para nada sobrar no navegador de quem desistiu.
export const VALIDADE_CONSULTA_MS = 5 * 60 * 1000

// Teto de chaves vivas simultâneas — guarda contra descontrole, NÃO a política
// de limpeza (quem limpa é a validade acima). O número tem que ficar acima da
// maior rajada de links que uma pessoa consegue gerar dentro da janela de
// validade, senão o teto vira bug: a chave é gravada quando a tela de feedback
// aparece, e em "Polêmicas"/"Jurisprudência" TODA pílula tem link — uma lição
// são 5 pílulas e uma revisão até 10 (PILULAS_POR_LICAO e MAX_PILULAS_REVISAO,
// em ./dominio). Com teto 5, quem abrisse o link da 1ª pílula em aba de fundo
// e seguisse a lição até o fim veria a própria chave expulsa pela 5ª — aba
// nova com o chat vazio, sem explicação nenhuma. 20 cobre uma revisão inteira
// com folga e custa ~50 KB de storage no pior caso.
const MAX_CONSULTAS = 20

interface Consulta {
    // timestamp da gravação — é o que permite expirar chave órfã
    t: number
    prompt: string
}

function chavesDeConsulta(): string[] {
    const chaves: string[] = []
    for (let i = 0; i < localStorage.length; i += 1) {
        const chave = localStorage.key(i)
        if (chave?.startsWith(PREFIXO)) chaves.push(chave)
    }
    return chaves
}

function lerConsulta(chave: string): Consulta | null {
    const bruto = localStorage.getItem(chave)
    if (!bruto) return null
    try {
        const c = JSON.parse(bruto)
        if (typeof c?.prompt !== 'string' || typeof c?.t !== 'number') {
            return null
        }
        return {t: c.t, prompt: c.prompt}
    } catch {
        return null
    }
}

// Chaves órfãs: a aba nova pode nunca ser aberta (a pessoa desiste, o popup é
// bloqueado, o navegador restaura a sessão sem ela). Não há evento de "aba
// fechada" em que confiar, então a limpeza é oportunista e roda nos dois
// momentos em que este módulo é exercitado: ao GRAVAR (na trilha) e ao LER (na
// abertura do Consultas). Só na gravação não bastaria — quem responde a última
// pílula da lição e fecha a aba deixaria o prompt parado no navegador até a
// próxima gravação, que pode não vir nunca. Descarta o que venceu ou não
// parseia e mantém só as mais recentes.
// `reservar` = uma nova está prestes a ser gravada, então abre-se uma vaga.
export function limparConsultas(reservar = false) {
    try {
        const agora = Date.now()
        const vivas: {chave: string; t: number}[] = []
        for (const chave of chavesDeConsulta()) {
            const c = lerConsulta(chave)
            if (!c || agora - c.t > VALIDADE_CONSULTA_MS) {
                localStorage.removeItem(chave)
                continue
            }
            vivas.push({chave, t: c.t})
        }
        vivas
            .sort((a, b) => b.t - a.t)
            .slice(Math.max(0, MAX_CONSULTAS - (reservar ? 1 : 0)))
            .forEach((v) => localStorage.removeItem(v.chave))
    } catch {
        // storage indisponível: não há o que limpar, e limpar nunca pode ser
        // o motivo de a tela não abrir
    }
}

// Grava o prompt e devolve a referência curta que vai na URL. `null` = o
// storage recusou (modo restrito, quota): quem chama precisa ter um plano B.
export function guardarConsulta(prompt: string): string | null {
    try {
        limparConsultas(true)
        // id curto e único o bastante para o par (aba que grava, aba que lê)
        // dentro da janela de validade: instante em base 36 + aleatório.
        // `padEnd` porque Math.random() ocasionalmente devolve um decimal
        // curto ('0.5' → slice vazio) e o id ficaria só com o instante — dois
        // links montados no mesmo milissegundo colidiriam.
        const id = `${Date.now().toString(36)}${Math.random()
            .toString(36)
            .slice(2, 8)
            .padEnd(6, '0')}`
        localStorage.setItem(
            PREFIXO + id,
            JSON.stringify({t: Date.now(), prompt})
        )
        return id
    } catch {
        return null
    }
}

// Lê e APAGA. Devolve `null` quando a chave não existe (storage limpo, link
// colado depois, aba já aberta uma vez) ou quando venceu — nos dois casos o
// chat abre normalmente, vazio.
export function consumirConsulta(id: string): string | null {
    if (!id) return null
    try {
        const chave = PREFIXO + id
        const c = lerConsulta(chave)
        // O remove vem antes de qualquer decisão: chave vencida ou corrompida
        // também some, senão viraria lixo permanente no navegador.
        localStorage.removeItem(chave)
        if (!c || Date.now() - c.t > VALIDADE_CONSULTA_MS) return null
        return c.prompt
    } catch {
        return null
    }
}

// O que a Home coloca no input ao abrir. `consulta` (transporte novo) tem
// precedência sobre `pergunta` (transporte antigo); sem nenhum dos dois, ou
// com referência inválida, devolve string vazia e o chat abre como sempre.
// Aproveita a passagem para varrer as órfãs: abrir o Consultas é a coisa que
// mais acontece no produto, e é o que garante que um prompt esquecido pela
// trilha não fique no navegador da pessoa além da sessão.
export function perguntaInicialDoChat(params: URLSearchParams): string {
    const referencia = params.get(PARAM_CONSULTA)
    const pergunta = referencia
        ? (consumirConsulta(referencia) ?? '')
        : (params.get(PARAM_PERGUNTA) ?? '')
    limparConsultas()
    return pergunta
}
